import { Box, Chip, Grid, IconButton, SelectChangeEvent, Stack, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { Analytics, CardStats } from "../../types/analytics";
import Chart from "./Chart";
import { parseData } from "./dashboard-helper";
import AnalyticCards from "./AnalyticCards";
import RecentSection from "./RecentSection";
import GreetingHeader from "./GreetingHeader";
import StudentPipelineWidget from "./StudentPipelineWidget";
import SubmissionFunnelWidget from "./SubmissionFunnelWidget";
import DueTodayBanner from "./DueTodayBanner";
import CommissionCard from "./CommissionCard";
import ActivityFeedWidget from "./ActivityFeedWidget";
import RevenueOverviewWidget from "./RevenueOverviewWidget";
import UpcomingDeadlinesWidget from "./UpcomingDeadlinesWidget";
import EmployeeWorkloadWidget from "./EmployeeWorkloadWidget";
import ConversionFunnelWidget from "./ConversionFunnelWidget";
import TopUniversitiesWidget from "./TopUniversitiesWidget";
import MonthlyTargetsWidget from "./MonthlyTargetsWidget";
import { DragIndicator, VisibilityOff, Visibility } from "@mui/icons-material";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { useAppTheme } from "../../context/ThemeContext";

type Periods = "daily" | "monthly" | "weekly";
type SectionId =
  | "stats"
  | "student-pipeline"
  | "submission-funnel"
  | "chart"
  | "cards"
  | "activity"
  | "revenue"
  | "deadlines"
  | "workload"
  | "funnel"
  | "universities"
  | "targets";

const DEFAULT_SECTION_ORDER: SectionId[] = [
  "stats",
  "revenue",
  "targets",
  "student-pipeline",
  "submission-funnel",
  "funnel",
  "chart",
  "workload",
  "universities",
  "activity",
  "deadlines",
  "cards",
];
const DEFAULT_CARD_ORDER = ["tasks", "reminders", "submissions", "passport"];

const SECTION_LABELS: Record<SectionId, string> = {
  stats: "Stat Cards",
  "student-pipeline": "Student Pipeline",
  "submission-funnel": "Submission Funnel",
  chart: "Trend Chart",
  cards: "Recent Cards",
  activity: "Activity Feed",
  revenue: "Revenue Overview",
  deadlines: "Upcoming Deadlines",
  workload: "Employee Workload",
  funnel: "Conversion Funnel",
  universities: "Top Universities",
  targets: "Monthly Targets",
};

function loadOrder<T extends string>(key: string, defaults: T[]): T[] {
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? "null");
    if (Array.isArray(saved) && saved.length === defaults.length) return saved as T[];
  } catch { /* ignore */ }
  return defaults;
}

function loadHidden(key: string): Set<string> {
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? "null");
    if (Array.isArray(saved)) return new Set(saved);
  } catch { /* ignore */ }
  return new Set();
}

// ── Draggable section wrapper ────────────────────────────────────

function DraggableSection({
  id,
  label,
  editMode,
  hidden,
  isDragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onToggleVisibility,
  children,
}: {
  id: string;
  label: string;
  editMode: boolean;
  hidden: boolean;
  isDragOver: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onToggleVisibility: () => void;
  children: React.ReactNode;
}) {
  if (hidden && !editMode) return null;

  if (hidden && editMode) {
    return (
      <Box
        onDragOver={(e) => { e.preventDefault(); }}
        mb={1}
        sx={{
          borderRadius: "8px",
          border: "2px dashed #E2E8F0",
          bgcolor: "#F8FAFC",
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <VisibilityOff sx={{ fontSize: 16, color: "#CBD5E1" }} />
          <Typography variant="body2" color="text.disabled" fontWeight={500}>
            {label}
          </Typography>
          <Chip label="hidden" size="small" sx={{ height: 18, fontSize: "0.62rem", color: "#94A3B8", bgcolor: "#F1F5F9" }} />
        </Stack>
        <Tooltip title="Show section" arrow>
          <IconButton size="small" onClick={onToggleVisibility} sx={{ color: "#94A3B8", "&:hover": { color: "#059669" } }}>
            <Visibility sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      draggable={editMode}
      onDragStart={editMode ? onDragStart : undefined}
      onDragOver={editMode ? onDragOver : undefined}
      onDrop={editMode ? onDrop : undefined}
      sx={{
        position: "relative",
        borderRadius: "10px",
        outline: editMode
          ? isDragOver ? "2px dashed #0EA5E9" : "2px dashed #CBD5E1"
          : "none",
        bgcolor: isDragOver ? "#EFF6FF" : "transparent",
        transition: "background 0.15s, outline-color 0.15s",
        cursor: editMode ? "grab" : "default",
        "&:active": editMode ? { cursor: "grabbing" } : {},
        p: editMode ? 0.5 : 0,
      }}
    >
      {editMode && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 20,
            bgcolor: "white",
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            px: 0.75,
            py: 0.25,
          }}
        >
          <Tooltip title="Hide section" arrow>
            <IconButton
              size="small"
              onClick={onToggleVisibility}
              sx={{ p: 0.25, color: "#94A3B8", "&:hover": { color: "#EF4444" } }}
            >
              <VisibilityOff sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <DragIndicator sx={{ fontSize: 16, color: "#CBD5E1" }} />
        </Stack>
      )}
      {children}
    </Box>
  );
}

// ── Main ─────────────────────────────────────────────────────────

function Dashboard({ analytics }: { analytics: Analytics }) {
  const [period, setPeriod] = useState<Periods>("daily");
  const cardsData: CardStats = parseData(analytics, period);
  const { isNewTheme, accentColor } = useAppTheme();
  const gap = isNewTheme ? 2.5 : 2;

  const role = useSelector((state: RootState) => state.user.role);
  const permissions = useSelector((state: RootState) => state.user.permissions);
  const allowedResources = permissions.map((res) => {
    if (res.resource === "tasks" && role === "admin") return false;
    if (res.actions.includes("READ")) return res.resource;
  });

  const canViewFinancials =
    role === "admin" ||
    permissions.some(
      (p) =>
        (p.resource === "transactions" || p.resource === "analytics") &&
        p.actions.includes("WRITE")
    );

  const [editMode, setEditMode] = useState(false);
  const [sectionOrder, setSectionOrder] = useState<SectionId[]>(() =>
    loadOrder("dashboard-section-order", DEFAULT_SECTION_ORDER)
  );
  const [cardOrder, setCardOrder] = useState<string[]>(() =>
    loadOrder("dashboard-card-order", DEFAULT_CARD_ORDER)
  );
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(() =>
    loadHidden("dashboard-hidden-sections")
  );
  const [hiddenCards, setHiddenCards] = useState<Set<string>>(() =>
    loadHidden("dashboard-hidden-cards")
  );
  const [dragOverSection, setDragOverSection] = useState<SectionId | null>(null);
  const draggingSection = useRef<SectionId | null>(null);

  const handleSelectPeriod = (e: SelectChangeEvent) => {
    setPeriod(e.target.value as Periods);
  };

  const handleSectionDrop = (target: SectionId) => {
    setDragOverSection(null);
    const from = draggingSection.current;
    if (!from || from === target) return;
    const next = [...sectionOrder];
    const fi = next.indexOf(from);
    const ti = next.indexOf(target);
    next.splice(fi, 1);
    next.splice(ti, 0, from);
    setSectionOrder(next);
    localStorage.setItem("dashboard-section-order", JSON.stringify(next));
    draggingSection.current = null;
  };

  const toggleSection = (id: string) => {
    setHiddenSections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem("dashboard-hidden-sections", JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const handleCardReorder = (next: string[]) => {
    setCardOrder(next);
    localStorage.setItem("dashboard-card-order", JSON.stringify(next));
  };

  const toggleCard = (id: string) => {
    setHiddenCards((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem("dashboard-hidden-cards", JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const chartSx = isNewTheme
    ? {
        bgcolor: "#ffffff",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        borderRadius: "8px",
        p: 2.5,
        height: 380,
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : {
        bgcolor: "#ffffff",
        boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
        borderRadius: "8px",
        p: 2,
        height: 380,
      };

  const sectionContent: Record<SectionId, React.ReactNode> = {
    stats: (
      <Grid container spacing={gap} alignItems="stretch">
        <Grid item xs={12} lg={canViewFinancials ? 9 : 12}>
          <AnalyticCards
            analytics={analytics}
            cardsData={cardsData}
            period={period}
            allowedResources={allowedResources}
          />
        </Grid>
        {canViewFinancials && (
          <Grid item xs={12} sm={6} lg={3}>
            <CommissionCard />
          </Grid>
        )}
      </Grid>
    ),
    revenue: canViewFinancials ? <RevenueOverviewWidget /> : null,
    "student-pipeline": <StudentPipelineWidget />,
    "submission-funnel": <SubmissionFunnelWidget />,
    activity: <ActivityFeedWidget />,
    deadlines: <UpcomingDeadlinesWidget />,
    workload: <EmployeeWorkloadWidget />,
    funnel: <ConversionFunnelWidget />,
    universities: <TopUniversitiesWidget />,
    targets: <MonthlyTargetsWidget />,
    chart: (
      <Box sx={chartSx}>
        <Chart handleSelectPeriod={handleSelectPeriod} period={period} data={cardsData} />
      </Box>
    ),
    cards: (
      <RecentSection
        cardOrder={cardOrder}
        hiddenCards={hiddenCards}
        editMode={editMode}
        onReorder={handleCardReorder}
        onToggleCard={toggleCard}
      />
    ),
  };

  return (
    <Box sx={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>

      <GreetingHeader editMode={editMode} onToggleEdit={() => setEditMode((v) => !v)} />

      <DueTodayBanner />

      {sectionOrder
        .filter((id) => {
          if (id === "revenue" || id === "workload" || id === "targets") return canViewFinancials;
          return true;
        })
        .map((id) => (
          <DraggableSection
            key={id}
            id={id}
            label={SECTION_LABELS[id]}
            editMode={editMode}
            hidden={hiddenSections.has(id)}
            isDragOver={dragOverSection === id}
            onDragStart={() => { draggingSection.current = id; }}
            onDragOver={(e) => { e.preventDefault(); setDragOverSection(id); }}
            onDrop={() => handleSectionDrop(id)}
            onToggleVisibility={() => toggleSection(id)}
          >
            <Box mb={gap}>
              {sectionContent[id]}
            </Box>
          </DraggableSection>
        ))}

    </Box>
  );
}

export default Dashboard;
